/* Fitness state sync helpers: a three-way merge used when the server copy
 * moved on while this device still had unsynced edits (another device, or
 * Claude writing through the MCP server).
 *
 *   base   = the server copy this device last synced with
 *   local  = what this device has now
 *   server = the current server copy
 *
 * Anything only one side changed is taken from that side. When both sides
 * changed the same thing differently, the server wins for that part and the
 * path is reported as a conflict so the UI can say so. Plain objects merge
 * per key; arrays of objects keyed by `id` (plan days, exercises, foods,
 * meal entries) or by `date` (bodyMetrics) merge per item; anything else is
 * replaced as a whole. Pure functions, no React. */

function isPlainObject(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/* Structural equality that ignores object key order (Python and JS can
 * serialise the same object with keys in a different order). */
export function deepEqual(a, b) {
  if (a === b) return true;
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) if (!deepEqual(a[i], b[i])) return false;
    return true;
  }
  if (isPlainObject(a)) {
    if (!isPlainObject(b)) return false;
    const ka = Object.keys(a);
    if (ka.length !== Object.keys(b).length) return false;
    for (const k of ka) {
      if (!Object.prototype.hasOwnProperty.call(b, k) || !deepEqual(a[k], b[k])) return false;
    }
    return true;
  }
  return false;
}

/* The field an array's items can be matched on, or null. */
function itemKeyField(...arrays) {
  for (const field of ['id', 'date']) {
    const ok = arrays.every(arr => {
      const seen = new Set();
      return arr.every(item => {
        if (!isPlainObject(item)) return false;
        const k = item[field];
        if ((typeof k !== 'string' && typeof k !== 'number') || seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    });
    if (ok) return field;
  }
  return null;
}

const has = (o, k) => Object.prototype.hasOwnProperty.call(o, k);

/* Merge one entry that may be missing on some sides. Returns
 * { keep: bool, value }. */
function mergeEntry(inBase, b, inLocal, l, inServer, s, conflicts, path) {
  if (!inLocal && !inServer) return { keep: false };
  if (!inLocal) {
    if (!inBase) return { keep: true, value: s }; // server added
    if (deepEqual(s, b)) return { keep: false }; // local removed, server untouched
    conflicts.push(path); // local removed, server edited: server wins
    return { keep: true, value: s };
  }
  if (!inServer) {
    if (!inBase) return { keep: true, value: l }; // local added
    if (deepEqual(l, b)) return { keep: false }; // server removed, local untouched
    conflicts.push(path); // server removed, local edited: server wins
    return { keep: false };
  }
  return { keep: true, value: merge3(inBase ? b : undefined, l, s, conflicts, path) };
}

export function merge3(base, local, server, conflicts = [], path = '') {
  if (deepEqual(local, server)) return server;
  if (deepEqual(local, base)) return server; // only the server changed
  if (deepEqual(server, base)) return local; // only this device changed

  if (isPlainObject(local) && isPlainObject(server)) {
    const b = isPlainObject(base) ? base : {};
    const out = {};
    const keys = [...Object.keys(server), ...Object.keys(local).filter(k => !has(server, k))];
    for (const k of keys) {
      const r = mergeEntry(has(b, k), b[k], has(local, k), local[k], has(server, k), server[k],
        conflicts, path ? `${path}.${k}` : k);
      if (r.keep) out[k] = r.value;
    }
    return out;
  }

  if (Array.isArray(local) && Array.isArray(server)) {
    const b = Array.isArray(base) ? base : [];
    const field = itemKeyField(b, local, server);
    if (field) {
      const index = arr => new Map(arr.map(item => [item[field], item]));
      const bm = index(b); const lm = index(local); const sm = index(server);
      const order = [...server.map(i => i[field]), ...local.map(i => i[field]).filter(k => !sm.has(k))];
      const out = [];
      for (const k of order) {
        const r = mergeEntry(bm.has(k), bm.get(k), lm.has(k), lm.get(k), sm.has(k), sm.get(k),
          conflicts, `${path}[${field}=${k}]`);
        if (r.keep) out.push(r.value);
      }
      return field === 'date' ? out.sort((x, y) => String(x.date).localeCompare(String(y.date))) : out;
    }
  }

  conflicts.push(path || '(root)');
  return server;
}

/* The part of the state that is synced to the server. Progress photos and
 * the rest timer are device-local. */
export function serverPart(state) {
  if (!state || typeof state !== 'object') return state;
  const { progressPhotos, restTimer, ...rest } = state;
  return rest;
}

/* JSON with object keys sorted, so equal states serialise identically. */
function stableStringify(v) {
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(',')}]`;
  if (isPlainObject(v)) {
    return `{${Object.keys(v).sort().map(k => `${JSON.stringify(k)}:${stableStringify(v[k])}`).join(',')}}`;
  }
  return JSON.stringify(v === undefined ? null : v);
}

/* Short fingerprint of a state (cyrb53). Lets the client recognise its own
 * write on the server when the response to that PUT never arrived, without
 * keeping a second full copy in localStorage. */
export function stateHash(value) {
  const str = stableStringify(value);
  let h1 = 0xdeadbeef; let h2 = 0x41c6ce57;
  for (let i = 0; i < str.length; i += 1) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}
